<script setup lang="ts">
import { computed, ref } from "vue";
import dayjs from "dayjs";
import { formatNumber, formatNumberForScale } from "chartjs-graph";
import LocaleNumberInput from "@/ui/components/LocaleNumberInput.vue";
import { useBasePriceStore, BasePriceBook } from "@/modules/base_price/store";
import PlanBudgetBooksDialog from "@/modules/base_price/components/PlanBudgetBooksDialog.vue";
import PlanIssuesDialog from "@/modules/base_price/components/PlanIssuesDialog.vue";

const store = useBasePriceStore();

const emit = defineEmits<{
	/** Handled by the editor: refreshing saves a new version and navigates to it. */
	(e: "refresh-impressions"): void;
}>();

const impressionsChangedTooltip = computed(() => {
	const capturedAt = store.impressionsStatus?.captured_at;
	const since = capturedAt
		? `taken on ${dayjs(capturedAt).format("DD MMM YYYY HH:mm")}`
		: "this version was saved with";

	return `The impressions have changed. This version still prices against the figures ${since}. Refresh to save a new version with the current impressions.`;
});

const target = computed<number>({
	get: () => store.plan?.target ?? 0,
	set: (value) => store.setTarget(value),
});

const booksDialogOpen = ref(false);
const issuesDialogOpen = ref(false);

const bookDotColor = (book: BasePriceBook) =>
	book.type === "open_inventory"
		? "rgb(var(--v-theme-primary))"
		: "rgb(var(--v-theme-warning))";
</script>

<template>
	<div v-if="store.plan">
		<div class="d-flex flex-wrap ga-8 align-start pa-4 border-b">
			<div class="flex-grow-1" style="min-width: 220px">
				<div class="text-caption text-medium-emphasis text-uppercase mb-1">
					Rate card planner &middot; FY{{ store.plan.year }}
				</div>
				<div class="text-h6 font-weight-bold">{{ store.plan.name }}</div>
			</div>

			<div>
				<div class="stat-label">Revenue target</div>
				<div class="stat-control">
					<locale-number-input
						v-model="target"
						format="money_no_fraction"
						density="compact"
						variant="outlined"
						hide-details
						style="width: 160px"
					/>
				</div>
			</div>

			<div>
				<div class="stat-label">Blended CPM</div>
				<div class="stat-control text-h6 font-weight-bold">
					{{
						store.blendedCpm !== null
							? formatNumber(store.blendedCpm, "money")
							: "—"
					}}
				</div>
			</div>

			<div>
				<div class="stat-label">Expected revenue</div>
				<div class="stat-control text-h6 font-weight-bold">
					{{ formatNumberForScale("money", store.expectedRevenue) }}
				</div>
			</div>

			<div>
				<div class="stat-label">Variance to target</div>
				<div class="stat-control">
					<v-chip
						:color="store.varianceToTarget.color"
						label
						density="comfortable"
					>
						{{ store.varianceToTarget.label }}
					</v-chip>
				</div>
			</div>

			<!--
				Refresh only on the live version when the source has moved (see
				store.impressionsChanged); otherwise the total the plan prices
				against.
			-->
			<div>
				<div class="stat-label">Impressions</div>
				<div v-if="store.impressionsChanged" class="stat-control">
					<v-btn
						v-tooltip:bottom="impressionsChangedTooltip"
						color="warning"
						variant="tonal"
						size="small"
						prepend-icon="fa-light fa-arrows-rotate"
						:loading="store.refreshingImpressions"
						:disabled="store.hasAllocationErrors"
						data-testid="refresh-impressions"
						@click="emit('refresh-impressions')"
					>
						Changed &middot; Refresh
					</v-btn>
				</div>
				<div v-else class="stat-control text-h6 font-weight-bold">
					{{
						store.grossImpressions !== null
							? formatNumberForScale("number_no_fraction", store.grossImpressions)
							: "—"
					}}
				</div>
			</div>

			<div>
				<div class="stat-label">&nbsp;</div>
				<div class="stat-control">
					<v-btn
						:color="store.planIssues.length ? 'error' : undefined"
						:variant="store.planIssues.length ? 'tonal' : 'outlined'"
						:disabled="!store.planIssues.length"
						size="small"
						prepend-icon="fa-light fa-triangle-exclamation"
						@click="issuesDialogOpen = true"
					>
						{{ store.planIssues.length || "0" }}
					</v-btn>
				</div>
			</div>
		</div>

		<div class="d-flex flex-wrap ga-4 align-start pa-4 border-b">
			<div
				class="text-caption text-medium-emphasis text-uppercase pt-2"
				style="white-space: nowrap"
			>
				Budget book
			</div>

			<div class="d-flex flex-wrap ga-3">
				<v-card
					v-for="book in store.books"
					:key="book.uuid"
					:variant="store.selectedBookUuid === book.uuid ? 'tonal' : 'outlined'"
					:color="store.selectedBookUuid === book.uuid ? 'primary' : undefined"
					min-width="160"
					link
					@click="store.selectBook(book.uuid)"
				>
					<div class="pa-3">
						<div class="d-flex align-center ga-2 mb-1">
							<span
								class="book-dot"
								:style="{ background: bookDotColor(book) }"
							/>
							<span class="text-body-2 font-weight-medium">{{
								book.name
							}}</span>
						</div>
						<div class="text-caption text-medium-emphasis">
							{{ formatNumberForScale("money", store.bookBudget(book)) }}
							<span class="mx-1">&middot;</span>
							<span
								v-if="store.bookImpressions(book) !== null"
								title="Impressions — currently a deterministic placeholder, pending the real hierarchy/capacity resolver"
							>
								{{
									formatNumberForScale(
										"number_no_fraction",
										store.bookImpressions(book) as number
									)
								}}
								imp
							</span>
							<span v-else>— imp</span>
						</div>
					</div>
				</v-card>
			</div>

			<v-tooltip text="Budget books" location="bottom">
				<template v-slot:activator="{ props: tooltip }">
					<v-btn
						v-bind="tooltip"
						icon="fa-light fa-gear"
						size="small"
						variant="text"
						class="align-self-center"
						@click="booksDialogOpen = true"
					/>
				</template>
			</v-tooltip>
		</div>
		<plan-budget-books-dialog v-model:visible="booksDialogOpen" />
		<plan-issues-dialog v-model:visible="issuesDialogOpen" />
	</div>
</template>

<style scoped lang="scss">
// One label and one control row for every stat in the header. Without it each
// stat sized itself to its own content — a 40px input beside 20px text beside
// a chip — and with the row aligned to the top, none of the values sat on the
// same line.
.stat-label {
	font-size: 12px;
	line-height: 16px;
	color: rgba(var(--v-theme-on-surface), 0.6);
	margin-bottom: 4px;
	white-space: nowrap;
}

.stat-control {
	display: flex;
	align-items: center;
	min-height: 40px;
}

.book-dot {
	width: 8px;
	height: 8px;
	border-radius: 2px;
	flex: 0 0 auto;
}
</style>
